import type { PerformanceRow } from '@agio/core';
import { monthlyReturns } from './utils.js';

/** The benchmark engine's monthly figure for one fund's month (GET /benchmark). */
export interface EngineBenchmarkRow {
  date: string;
  fund_id: string;
  monthly_benchmark: number | null;
}

/**
 * A month that came from a published monthly statement rather than the
 * workbook. Publishing writes the statement's file name, prefixed `pdf:`, where
 * a workbook row has its sheet name.
 */
export const isStatementMonth = (p: Pick<PerformanceRow, 'sheet_name'>) => p.sheet_name.startsWith('pdf:');

/**
 * The returns of each fund's months, ready to show.
 *
 * A workbook month carries its returns and benchmark as the workbook printed
 * them (columns E-H, manual fixes included) and is passed through untouched.
 * A statement month carries only a balance and the month's deposits and
 * withdrawals, so its four figures are worked out here:
 *  - monthly return: from the balances and flows, the same formula as
 *    `monthlyReturns` (the workbook's own column E);
 *  - monthly benchmark: the benchmark engine's figure for that fund and month;
 *  - cumulative return / benchmark: the previous month's cumulative figure,
 *    compounded by this month's.
 *
 * Anything that cannot be worked out stays null and shows as missing — never 0.
 * No previous balance means no return; no engine figure means no benchmark; and
 * a missing month leaves every later cumulative figure missing too, since
 * compounding past an unknown month would print a wrong number.
 *
 * Rows come back in the order they were given.
 */
export function withStatementReturns(perf: PerformanceRow[], bench: EngineBenchmarkRow[]): PerformanceRow[] {
  const engine = new Map(bench.map((b) => [`${b.fund_id}|${b.date.slice(0, 7)}`, b.monthly_benchmark]));
  const byFund = new Map<string, PerformanceRow[]>();
  for (const p of perf) byFund.set(p.fund_id, [...(byFund.get(p.fund_id) ?? []), p]);

  const shown = new Map<PerformanceRow, PerformanceRow>();
  for (const rows of byFund.values()) {
    const sorted = [...rows].sort((a, b) => a.date.localeCompare(b.date));
    const computed = new Map(monthlyReturns(sorted).map((r) => [r.date, r.ret]));
    let cumulative: number | null = null;
    let benchCumulative: number | null = null;
    let prevDate: string | null = null;
    for (const row of sorted) {
      const followsOn = prevDate !== null && followsMonth(prevDate, row.date);
      prevDate = row.date;
      if (!isStatementMonth(row)) {
        shown.set(row, row);
        cumulative = row.cumulative_return;
        benchCumulative = row.benchmark_cumulative;
        continue;
      }
      // A month missing in between (e.g. a statement published without
      // confirmed flows writes no row) would fold two months into one figure —
      // a plausible wrong number. Leave this month missing instead.
      const ret = followsOn ? computed.get(row.date) ?? null : null;
      const benchMonthly = followsOn ? engine.get(`${row.fund_id}|${row.date.slice(0, 7)}`) ?? null : null;
      cumulative = cumulative !== null && ret !== null ? (1 + cumulative) * (1 + ret) - 1 : null;
      benchCumulative = benchCumulative !== null && benchMonthly !== null ? (1 + benchCumulative) * (1 + benchMonthly) - 1 : null;
      shown.set(row, {
        ...row,
        monthly_return: ret,
        cumulative_return: cumulative,
        benchmark_monthly: benchMonthly,
        benchmark_cumulative: benchCumulative,
      });
    }
  }
  return perf.map((p) => shown.get(p)!);
}

/** `date` is in the same calendar month as `prev`, or the month right after it. */
function followsMonth(prev: string, date: string): boolean {
  const [py, pm] = prev.slice(0, 7).split('-').map(Number);
  const [y, m] = date.slice(0, 7).split('-').map(Number);
  const gap = (y - py) * 12 + (m - pm);
  return gap === 0 || gap === 1;
}
